/**
 * React Query mutation hooks for TikTok product lifecycle actions.
 * Separate from useTikTokSave (which handles field edits).
 *
 * - useTikTokActivate: SELLER_DEACTIVATED/DRAFT → ACTIVATE
 * - useTikTokDeactivate: ACTIVATE → SELLER_DEACTIVATED
 * - useTikTokDelete: permanently remove from TikTok (irreversible)
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tiktokProductApi } from "../api/tiktokProductApi";
import { useTikTokEditStore } from "../stores/useTikTokEditStore";

export function useTikTokActivate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { connectionName, productId } = useTikTokEditStore.getState();
      if (!connectionName || !productId) throw new Error("Missing connection or product ID");
      return tiktokProductApi.activateProduct(connectionName, productId);
    },
    onSuccess() {
      const store = useTikTokEditStore.getState();
      // Must forceReinit so store picks up new status from fresh formData
      store.forceReinit();
      if (store.listingName) {
        queryClient.invalidateQueries({ queryKey: ["get", "TikTok Listing", store.listingName] });
      }
    },
  });
}

export function useTikTokDeactivate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { connectionName, productId } = useTikTokEditStore.getState();
      if (!connectionName || !productId) throw new Error("Missing connection or product ID");
      return tiktokProductApi.deactivateProduct(connectionName, productId);
    },
    onSuccess() {
      const store = useTikTokEditStore.getState();
      store.forceReinit();
      if (store.listingName) {
        queryClient.invalidateQueries({ queryKey: ["get", "TikTok Listing", store.listingName] });
      }
    },
  });
}

export function useTikTokDelete() {
  return useMutation({
    mutationFn: async () => {
      const { connectionName, productId } = useTikTokEditStore.getState();
      if (!connectionName || !productId) throw new Error("Missing connection or product ID");
      return tiktokProductApi.deleteProduct(connectionName, productId);
    },
    // No cache invalidation — we redirect away after delete
  });
}
